import React, { useMemo } from 'react';
import { DetectionParameter, SupportedDescriptors } from './types';

import { HudIcon } from '../Icons/HudIcons';
import { NumberDetectionParameter } from './RuleTypes/NumberDetectionParameter';

import * as Styled from './DetectionParametersGroup.styles';

export interface DetectionParametersGroupProps {
  detectionParams: DetectionParameter[];
  descriptors: SupportedDescriptors;
  onParameterChange?: (params: DetectionParameter[]) => void;

  readonly?: boolean;
  fullWidth?: boolean;
}

export function DetectionParametersGroup({
  detectionParams,
  descriptors,
  onParameterChange,
  readonly,
  fullWidth,
  ...rest
}: DetectionParametersGroupProps) {
  function handleParameterChange(index: number, param: DetectionParameter) {
    const newParams = [...detectionParams];
    newParams[index] = param;
    onParameterChange?.(newParams);
  }

  return (
    <Styled.Container {...rest}>
      {detectionParams.map((param, index) => (
        <Styled.TreeNodeContainer key={index}>
          <Styled.TreeNode>
            <SingleDetectionParameter
              detectionParameter={param}
              descriptors={descriptors}
              readonly={readonly}
              onParameterChange={(param) => handleParameterChange(index, param)}
              fullWidth={fullWidth}
            />
          </Styled.TreeNode>
        </Styled.TreeNodeContainer>
      ))}
    </Styled.Container>
  );
}

export interface SingleDetectionParameterProps {
  detectionParameter: DetectionParameter;
  descriptors: SupportedDescriptors;
  readonly?: boolean;
  onParameterChange?: (param: DetectionParameter) => void;

  fullWidth?: boolean;
  dirty?: boolean;
  role?: string;
}

export function SingleDetectionParameter({
  detectionParameter,
  descriptors,
  readonly,
  onParameterChange,
  fullWidth,
}: Omit<SingleDetectionParameterProps, 'dirty'>) {
  const initialValue = useMemo(() => detectionParameter.value, []);
  const dirty = detectionParameter.value !== initialValue;

  if (!(detectionParameter.id in descriptors)) {
    return (
      <Styled.ErrorFilter>
        <HudIcon icon="other.errorNotification" />
        Missing filter descriptor
      </Styled.ErrorFilter>
    );
  }

  const parameterType = descriptors[detectionParameter.id].type;
  if (parameterType === 'number') {
    return (
      <NumberDetectionParameter
        detectionParameter={detectionParameter}
        descriptors={descriptors}
        readonly={readonly}
        onParameterChange={onParameterChange}
        fullWidth={fullWidth}
        dirty={dirty}
      />
    );
  }

  return (
    <Styled.ErrorFilter>
      <HudIcon icon="other.errorNotification" /> Filter type is not implemented
    </Styled.ErrorFilter>
  );
}
